import { Message, Tag } from 'lib-shared-legacy-types';

import { Maybe } from '../apis/graphQl/elevio/grappa/types/grappa.types';
import { ConversationThread, ParsedMessage, Thread } from '../types/messages';

import { getPreparedTags } from './index';

/** Subset of Dixa Message type, with just 5 key values used */
export type SubMessage = Pick<
  Message,
  'author' | 'direction' | 'text' | 'created_at' | 'channel' | 'type'
>;

/**
 * Extracts end user converstation message content
 *
 * @param messages Dixa conversation messages
 */
export function extractConversationTextFromMessages(
  messages: SubMessage[],
): string {
  const userMessages = messages.reduce((prev, msg) => {
    if (!!msg.text && msg.direction === 'inbound') {
      return prev.concat(`${msg.text}\n\n`);
    }
    return prev;
  }, '');

  return userMessages;
}

type MergeConversationTextWithSubjectParams = {
  conversationSubject?: string;
  conversationText: string;
};
/**
 * Merge conversation subject with conversation text in format required to send
 * to grappa api
 */
export function mergeConversationTextWithSubject({
  conversationSubject,
  conversationText,
}: MergeConversationTextWithSubjectParams): string {
  if (!conversationSubject) return conversationText;
  return `${conversationSubject}\n\n${conversationText}`;
}

/**
 * Extracts tag names from tags
 *
 * @param tags Dixa conversation tags
 */
export function extractTagNames(tags: Tag[] | undefined): string[] {
  return tags?.map((t) => t.name) || [];
}

/**
 * Extracts end user converstation message content
 *
 * @param messages Dixa conversation messages
 */
export function convertMessagesToParsedMessages(
  messages: SubMessage[],
): ParsedMessage[] {
  const initialMessages: ParsedMessage[] = [];
  const parsedMessages = messages.reduce(
    (prev: ParsedMessage[], msg: SubMessage) => {
      if (
        !!msg.text &&
        msg.direction === 'inbound' &&
        msg.created_at &&
        msg.channel
      ) {
        const parsedMessage: ParsedMessage = {
          message: msg.text,
          sender_type: 'user',
          timestamp: new Date(msg.created_at).toISOString(), // 2018-11-13T20:20:39+00:00
          type: msg.channel,
        };
        prev.push(parsedMessage);
      }
      return prev;
    },
    initialMessages,
  );

  return parsedMessages;
}

export function getTruncatedMessage(
  message: Maybe<string>,
  threshold = 4997,
): Maybe<string> {
  // Cater for null message
  if (!message) return '';
  // Indicate that message has been truncated with ...
  // Threshold is not always 4997 as sometimes the attachment html length needs to be accounted for
  return message.length > 5000 ? message.slice(0, threshold) + '...' : message;
}

export function transformMessagesToConversationThread(
  messages: Message[],
): ConversationThread {
  const thread: ConversationThread = messages.reduce<ConversationThread>(
    (acc, conversationMessage) => {
      const { text, created_at, direction, type } = conversationMessage;

      if (
        !text ||
        !created_at ||
        type === 'conversation:wrapup_message' || // internal note
        type === 'conversation:wrapup_added' // internal note
      )
        return acc;

      const thread: Thread = {
        message: getTruncatedMessage(text),
        timestamp: created_at, // 2019-01-28T22:33:48.196Z - this format is important for the analytics to work, and is what is provided by the Dixa API
        sender_type: direction === 'outbound' ? 'agent' : 'user',
      };
      return acc.concat(thread);
    },
    [],
  );

  return thread;
}

export function getDraftTicketThread({
  url: ticketLink,
  tagNames,
  notes,
  articleContent,
}: {
  url: string;
  tagNames: readonly string[];
  notes: string;
  articleContent: string;
}): ConversationThread {
  const date = new Date();
  const preparedTags = getPreparedTags(tagNames);
  const ticketLinkMessage: Thread = {
    message: `<blockquote class="elevio-box-info"><b>Ticket Link:</b><br/><a href="${ticketLink}" target="_blank">${ticketLink}</a></blockquote>`,
    timestamp: date.toISOString(),
    sender_type: 'custom',
  };

  const tagsMessage: Thread = {
    message: `<blockquote class="elevio-box-info"><b>Tags:</b><br/>${preparedTags}</blockquote>`,
    timestamp: date.toISOString(),
    sender_type: 'custom',
  };

  const customNoteMessage: Thread = {
    message: `<blockquote class="elevio-box-info"><b>Notes:</b><br/>${notes}<br/></blockquote>`,
    timestamp: date.toISOString(),
    sender_type: 'custom',
  };

  const draftTicketThread: Thread = composeThreadFromArticleContent({
    articleContent,
  });

  const ticketThreadWithCustomMessages = [
    ticketLinkMessage,
    tagsMessage,
    customNoteMessage,
    draftTicketThread,
  ];

  return ticketThreadWithCustomMessages;
}

/** Composes html string from messages */
export function composeArticleContentFromMessages(
  messages: SubMessage[],
): string {
  const htmlString = messages.reduce((prev, currentMessage) => {
    const { type, text, direction } = currentMessage;
    if (
      !text || // discard any blank messages
      type === 'conversation:wrapup_message' // don't include internal notes
    ) {
      return prev;
    }
    const user = direction === 'inbound' ? 'User' : 'Agent';
    return prev.concat(`<b>${user} wrote:</b><br/>${text}<br/><hr/>`);
  }, '');
  return htmlString;
}

function composeThreadFromArticleContent({
  articleContent,
}: {
  articleContent: string;
}): Thread {
  const thread: Thread = {
    message: articleContent,
    timestamp: new Date().toISOString(),
    sender_type: 'agent',
  };
  return thread;
}
